import React, { useRef } from 'react';

/**
 * TableRow component for rendering a single row in the Table
 * @param {Object} props
 * @param {Object} props.row - The row data object
 * @param {number} props.rowIndex - The index of the row
 * @param {Array} props.columns - The columns definition array
 * @param {string} props.keyField - The key field for the row
 * @param {boolean} props.selectable - Whether the row is selectable
 * @param {boolean} props.isSelected - Whether the row is selected
 * @param {Function} props.handleRowSelect - Handler for row selection
 * @param {Function} props.onRowClick - Handler for row click
 * @param {boolean} props.draggable - Whether the row is draggable
 * @param {boolean} props.isDragging - Whether the row is currently being dragged
 * @param {Function} props.onDragStart - Handler for drag start
 * @param {Function} props.onDragEnd - Handler for drag end
 */
function TableRow({
                      row,
                      rowIndex,
                      columns,
                      keyField,
                      selectable,
                      isSelected,
                      handleRowSelect,
                      onRowClick,
                      draggable = false,
                      isDragging = false,
                      onDragStart,
                      onDragEnd
                  }) {
    const rowKey = row[keyField];
    const ref = useRef(null);

    // Handle drag start
    const handleDragStart = (e) => {
        if (!draggable || !onDragStart) return;
        onDragStart(e, row, rowIndex);
    };

    // Handle drag end
    const handleDragEnd = (e) => {
        if (!draggable || !onDragEnd) return;
        onDragEnd(e);
    };

    // Handle row click (prevent when dragging)
    const handleRowClick = () => {
        if (isDragging) return;
        if (onRowClick) onRowClick(row, rowIndex);
    };

    return (
        <tr
            ref={ref}
            draggable={draggable}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onClick={handleRowClick}
            className={`
                ${isSelected ? 'selected' : ''}
                ${onRowClick ? 'clickable' : ''}
                ${draggable ? 'draggable-row' : ''}
                ${isDragging ? 'dragging' : ''}
            `.trim()}
            style={{
                opacity: isDragging ? 0.5 : 1,
                cursor: draggable ? 'move' : 'default',
                userSelect: draggable ? 'none' : 'auto'
            }}
        >
            {selectable && (
                <td
                    data-label="Select"
                    className="select-column"
                    style={{ textAlign: 'center', width: '50px' }}
                >
                    <input
                        name={`row-select-${rowKey}`}
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => handleRowSelect(rowKey, e.target.checked)}
                        onClick={e => e.stopPropagation()}
                    />
                </td>
            )}
            {columns.map((column) => (
                <td
                    id={`r-tr-${rowKey}-${column.key}-${rowIndex}`}
                    data-label={(column.label || column.header || column.key).toString()}
                    key={column.key}>
                    {column.render
                        ? column.render(row[column.key], row, rowIndex)
                        : row[column.key]}
                </td>
            ))}
        </tr>
    );
}

export default TableRow;
